import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import AdminSidebar from "../components/AdminSidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Users,
  Briefcase,
  DollarSign,
  TrendingUp,
  Crown,
  Award,
  BarChart3,
  RefreshCw,
  Activity,
  Zap,
  MessageSquare,
  Video,
  FileText
} from "lucide-react";

const AdminAnalytics = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [overview, setOverview] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [featureUsage, setFeatureUsage] = useState(null);
  const [growth, setGrowth] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, subsRes, usageRes, growthRes] = await Promise.all([
        fetch(`${API}/admin/analytics/overview`, { credentials: "include" }),
        fetch(`${API}/admin/analytics/subscriptions`, { credentials: "include" }),
        fetch(`${API}/admin/analytics/feature-usage`, { credentials: "include" }),
        fetch(`${API}/admin/analytics/growth`, { credentials: "include" })
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (subsRes.ok) setSubscriptions(await subsRes.json());
      if (usageRes.ok) setFeatureUsage(await usageRes.json());
      if (growthRes.ok) setGrowth(await growthRes.json());
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success("Analytics refreshed");
  };

  if (user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-crimson mb-2">Access Denied</h1>
          <p className="text-gray-400">Admin access required</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex">
        <AdminSidebar />
        <main className="flex-1 lg:ml-64 p-8">
          <div className="flex items-center justify-center h-64">
            <div className="w-16 h-16 bg-crimson/20 flex items-center justify-center animate-pulse">
              <BarChart3 className="w-8 h-8 text-crimson" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <AdminSidebar />
      
      <main className="lg:ml-64 min-h-screen px-4 sm:px-6 pb-6 pt-20 lg:pt-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold mb-2">
                PLATFORM <span className="text-crimson">ANALYTICS</span>
              </h1>
              <p className="text-gray-400">Real-time platform metrics and insights</p>
            </div>
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              variant="outline"
              className="border-[#333] hover:border-crimson"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-obsidian-light border border-[#222] p-6"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-blue-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase">Total Users</p>
                  <p className="text-2xl font-bold text-white">{overview?.users?.total || 0}</p>
                </div>
              </div>
              <p className="text-green-400 text-xs">+{overview?.users?.new_this_week || 0} this week</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-obsidian-light border border-[#222] p-6"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-500/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase">Monthly Revenue</p>
                <p className="text-2xl font-bold text-white">
                  ${subscriptions?.monthly_recurring_revenue?.toLocaleString() || 0}
                </p>
              </div>
            </div>
            <p className="text-gray-500 text-xs">{subscriptions?.paid_users || 0} paid users</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-obsidian-light border border-[#222] p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-500/20 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase">Active Jobs</p>
                <p className="text-2xl font-bold text-white">{overview?.jobs?.active || 0}</p>
              </div>
            </div>
            <p className="text-green-400 text-xs">+{overview?.jobs?.new_this_month || 0} this month</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-obsidian-light border border-[#222] p-6"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-crimson/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-crimson" />
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase">Conversion</p>
                <p className="text-2xl font-bold text-white">{subscriptions?.conversion_rate || 0}%</p>
              </div>
            </div>
            <p className="text-gray-500 text-xs">Free to paid</p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Subscription Distribution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-obsidian-light border border-[#222] p-6"
          >
            <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
              <Crown className="w-5 h-5 text-crimson" />
              Subscription Tiers
            </h3>
            <div className="space-y-3">
              {subscriptions?.tier_distribution?.map((tier, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${tier.price > 0 ? 'bg-green-400' : 'bg-gray-500'}`} />
                    <span className="text-gray-300 text-sm">{tier.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-white font-medium">{tier.count}</span>
                    {tier.price > 0 && (
                      <span className="text-green-400 text-xs ml-2">${tier.monthly_revenue}/mo</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Feature Usage */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-obsidian-light border border-[#222] p-6"
          >
            <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-crimson" />
              Feature Usage
            </h3>
            <div className="space-y-3">
              {featureUsage?.feature_usage?.slice(0, 6).map((feature, idx) => (
                <div key={idx}>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-400 text-sm">{feature.feature_label}</span>
                    <span className="text-white text-sm">{feature.total_usage}</span>
                  </div>
                  <div className="h-1.5 bg-[#222] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-crimson to-red-500"
                      style={{ 
                        width: `${Math.min(100, (feature.total_usage / (featureUsage.feature_usage[0]?.total_usage || 1)) * 100)}%` 
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-[#333]">
              <p className="text-xs text-gray-500">
                ⚠️ {featureUsage?.limit_warnings_sent || 0} limit warnings sent this month
              </p>
            </div>
          </motion.div>

          {/* Quick Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-obsidian-light border border-[#222] p-6"
          >
            <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-crimson" />
              Platform Activity
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-obsidian border border-[#333]">
                <div className="flex items-center gap-2">
                  <Video className="w-4 h-4 text-purple-400" />
                  <span className="text-gray-300">Interviews</span>
                </div>
                <span className="text-white font-medium">{overview?.interviews?.this_month || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-obsidian border border-[#333]">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-400" />
                  <span className="text-gray-300">Applications</span>
                </div>
                <span className="text-white font-medium">{overview?.applications?.this_month || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-obsidian border border-[#333]">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-green-400" />
                  <span className="text-gray-300">Messages (7d)</span>
                </div>
                <span className="text-white font-medium">{overview?.engagement?.messages_this_week || 0}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Growth Chart Placeholder */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-obsidian-light border border-[#222] p-6 mt-6"
        >
          <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-crimson" />
            Weekly Growth
            <span className={`ml-auto text-sm ${growth?.wow_growth_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {growth?.wow_growth_percent >= 0 ? '↑' : '↓'} {Math.abs(growth?.wow_growth_percent || 0)}% WoW
            </span>
          </h3>
          <div className="grid grid-cols-7 gap-2">
            {growth?.daily_signups?.slice(-7).map((day, idx) => (
              <div key={idx} className="text-center">
                <div 
                  className="bg-crimson/20 mx-auto mb-2" 
                  style={{ 
                    width: '100%', 
                    height: `${Math.max(20, Math.min(100, day.signups * 20))}px`,
                    background: `linear-gradient(to top, #DC143C ${Math.min(100, day.signups * 20)}%, transparent ${Math.min(100, day.signups * 20)}%)`
                  }}
                />
                <p className="text-xs text-gray-500">{day.date.slice(-5)}</p>
                <p className="text-xs text-white">{day.signups}</p>
              </div>
            ))}
          </div>
        </motion.div>
        </div>
      </main>
    </div>
  );
};

export default AdminAnalytics;
